import { useGetCountryName } from 'hooks/ppoints/useGetCountryName';
import { CountryWithProviders } from 'models/ppoints/Country';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const ShippingProviderCountrySelect: React.FC<ShippingProviderCountrySelectProps> = ({
    value,
    onChange,
    disabled,
    countries,
    shopifyLocals,
    selectedProviders,
    labelHidden,
}) => {
    const { t } = useTranslation();
    const { getTranslatedCountryName: getCountryName } = useGetCountryName();

    const mapTopOption = (country: CountryWithProviders) => (
        <s-option key={country.country_code} value={country.country_code}>
            {getCountryName(country.country_code)}
        </s-option>
    );

    const locationGroups = useMemo(() => {
        const myLocations: CountryWithProviders[] = [];
        const usedLocations: CountryWithProviders[] = [];
        const otherCountries: CountryWithProviders[] = [];

        (countries ?? []).forEach((c) => {
            if (shopifyLocals?.includes(c.country_code)) {
                myLocations.push(c);
            } else if (selectedProviders?.[c.name]?.length > 0) {
                usedLocations.push(c);
            } else {
                otherCountries.push(c);
            }
        });
        otherCountries.sort((a, b) =>
            String(getCountryName(a.country_code)).localeCompare(String(getCountryName(b.country_code)))
        );

        return [
            { label: t('SHIPPING_PROVIDERS.MY_LOCATIONS'), options: myLocations.map(mapTopOption) },
            { label: t('SHIPPING_PROVIDERS.USED_LOCATIONS'), options: usedLocations.map(mapTopOption) },
            { label: t('SHIPPING_PROVIDERS.OTHER_LOCATIONS'), options: otherCountries.map(mapTopOption) },
        ].filter((group) => group.options.length > 0);
    }, [shopifyLocals, selectedProviders, countries, mapTopOption, getCountryName, t]);

    return (
        <s-select
            icon="globe"
            label={t('SHIPPING_PROVIDERS.COUNTRY_LABEL')}
            labelAccessibilityVisibility={labelHidden ? 'exclusive' : undefined}
            disabled={disabled || undefined}
            value={value ?? ''}
            onChange={(e) => onChange(e.currentTarget.value)}
        >
            {locationGroups.length > 1
                ? locationGroups.map((group) => (
                      <s-option-group key={group.label} label={group.label}>
                          {group.options}
                      </s-option-group>
                  ))
                : locationGroups[0]?.options}
        </s-select>
    );
};

export interface ShippingProviderCountrySelectProps {
    value: string;
    onChange: (countryCode: string) => void;
    disabled?: boolean;
    countries: CountryWithProviders[];
    shopifyLocals?: string[];
    selectedProviders?: ProviderToCountrySetting;
    labelHidden?: boolean;
}
